/**
 * imageEdit — Envío AP-4 · motor de «Editar imagen» (todo en el navegador).
 *
 * Recortar, fundido (transparencia en degradé), deformar con 4 puntos, forma,
 * borde y sombra, color, voltear/girar y anotar. El resultado es un PNG nuevo
 * (con transparencia) que se sube a la Galería: el renderer lo exporta como
 * cualquier imagen, así que preview y MP4 coinciden. El original nunca se toca.
 *
 * Coordenadas: todo en fracciones 0..1 de la imagen de SALIDA (después de
 * girar). El recorte y la forma se aplican sobre la imagen ya deformada.
 */

export const TOOLS = [
  ['rapidos', 'Rápidos'], ['recortar', 'Recortar'], ['fundido', 'Fundido'],
  ['deformar', 'Deformar'], ['forma', 'Forma'], ['borde', 'Borde'],
  ['color', 'Color'], ['voltear', 'Voltear'], ['anotar', 'Anotar'],
  ['tamano', 'Tamaño'],
];

// AP-10 · qué hace cada herramienta (franja de descripción y tooltip).
export const TOOL_TIPS = {
  rapidos: 'Ajustes de un toque: mitades, fundidos, viñeta, ventana con borde.',
  recortar: 'Quitá bordes o dibujá un recuadro. La imagen no se mueve.',
  fundido: 'Transparencia en degradé desde un lado o en viñeta.',
  deformar: 'Mové las 4 esquinas para poner la imagen en perspectiva.',
  forma: 'Redondeado, elipse o una forma libre con puntos.',
  borde: 'Marco de color y sombra suave.',
  color: 'Brillo, contraste, saturación, desenfoque, blanco y negro.',
  voltear: 'Espejo horizontal o vertical y giro de 90°.',
  anotar: 'Pincel, flechas, formas y texto para marcarle al modelo qué cambiar.',
  tamano: 'Resolución, formato y peso de la imagen final, listos para cada destino.',
};

export const FADE_SIDES = [
  ['none', 'Sin fundido'], ['left', '←'], ['right', '→'], ['top', '↑'], ['bottom', '↓'], ['radial', '◎'],
];

export const MARK_TOOLS = [
  ['path', 'Pincel'], ['arrow', 'Flecha'], ['rect', 'Rectángulo'],
  ['ellipse', 'Círculo'], ['text', 'Texto'], ['erase', 'Goma'],
];
export const MARK_TIPS = {
  path: 'Trazo a mano alzada.', arrow: 'Señalá un detalle.', rect: 'Encerrá una zona.',
  ellipse: 'Rodeá un elemento.', text: 'Escribí el pedido sobre la imagen.', erase: 'Tocá una marca para borrarla.',
};
export const MARK_COLORS = [
  ['#EF4444', 'Rojo'], ['#FBBF24', 'Amarillo'], ['#22D3EE', 'Celeste'], ['#FFFFFF', 'Blanco'], ['#111111', 'Negro'],
];

export const MAX_OUTPUT_PX = 4096;      // lado mayor de la imagen final
export const PREVIEW_PX = 1100;         // lado mayor del preview en vivo

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);
const IDENTITY_CORNERS = [[0, 0], [1, 0], [1, 1], [0, 1]];

export const DEFAULT_PARAMS = Object.freeze({
  crop: { t: 0, r: 0, b: 0, l: 0 },
  fade: { side: 'none', start: 45, soft: 25 },
  shape: { kind: 'rect', radius: 0, points: null },
  border: { enabled: false, color: '#FFFFFF', width: 6 },
  shadow: { enabled: false, strength: 60 },
  color: { brightness: 100, contrast: 100, saturation: 100, blur: 0, grayscale: false },
  flip: { h: false, v: false },
  rotate: 0,
  warp: null,
  marks: [],
  output: { preset: 'original', maxW: null, maxH: null, anyOrientation: true, format: 'png', quality: 0.92, maxMB: null },
});

export function defaultParams() {
  return normalizeParams({});
}

/** Valores seguros siempre (sirve para leer lo guardado en un clip). */
export function normalizeParams(raw) {
  const p = raw && typeof raw === 'object' ? raw : {};
  const c = p.crop || {};
  let crop = {
    t: clamp(num(c.t, 0), 0, 95), r: clamp(num(c.r, 0), 0, 95),
    b: clamp(num(c.b, 0), 0, 95), l: clamp(num(c.l, 0), 0, 95),
  };
  if (crop.l + crop.r > 95) crop = { ...crop, r: 95 - crop.l };
  if (crop.t + crop.b > 95) crop = { ...crop, b: 95 - crop.t };
  const f = p.fade || {};
  const side = FADE_SIDES.some(([k]) => k === f.side) ? f.side : 'none';
  const s = p.shape || {};
  const kind = ['rect', 'ellipse', 'polygon'].includes(s.kind) ? s.kind : 'rect';
  const points = Array.isArray(s.points) && s.points.length >= 3
    ? s.points.slice(0, 24).map(([x, y]) => [clamp(num(x, 0), 0, 1), clamp(num(y, 0), 0, 1)])
    : null;
  const b = p.border || {};
  const sh = p.shadow || {};
  const col = p.color || {};
  const rot = ((Math.round(num(p.rotate, 0) / 90) * 90) % 360 + 360) % 360;
  const warp = Array.isArray(p.warp) && p.warp.length === 4
    ? p.warp.map(([x, y]) => [clamp(num(x, 0), -0.5, 1.5), clamp(num(y, 0), -0.5, 1.5)])
    : null;
  return {
    crop,
    fade: { side, start: clamp(num(f.start, 45), 0, 100), soft: clamp(num(f.soft, 25), 1, 100) },
    shape: { kind, radius: clamp(num(s.radius, 0), 0, 50), points: kind === 'polygon' ? (points || defaultPolygon(crop)) : points },
    border: { enabled: !!b.enabled, color: /^#[0-9a-f]{6}$/i.test(b.color || '') ? b.color : '#FFFFFF', width: clamp(num(b.width, 6), 1, 40) },
    shadow: { enabled: !!sh.enabled, strength: clamp(num(sh.strength, 60), 10, 100) },
    color: {
      brightness: clamp(num(col.brightness, 100), 0, 200), contrast: clamp(num(col.contrast, 100), 0, 200),
      saturation: clamp(num(col.saturation, 100), 0, 300), blur: clamp(num(col.blur, 0), 0, 30),
      grayscale: !!col.grayscale,
    },
    flip: { h: !!(p.flip && p.flip.h), v: !!(p.flip && p.flip.v) },
    rotate: rot,
    warp: warp && !isIdentityCorners(warp) ? warp : null,
    marks: Array.isArray(p.marks) ? p.marks.filter(isValidMark).slice(0, 200) : [],
    output: normalizeOutput(p.output),
  };
}

// ── AP-14 · Tamaño: resolución, formato y peso de la salida ──────────────

export const OUTPUT_FORMATS = [['png', 'PNG'], ['jpeg', 'JPG'], ['webp', 'WebP']];
const MIME = { png: 'image/png', jpeg: 'image/jpeg', webp: 'image/webp' };
const EXT = { png: 'png', jpeg: 'jpg', webp: 'webp' };

/** Ajustes por destino. `anyOrientation`: el recuadro vale vertical u horizontal. */
export const OUTPUT_PRESETS = [
  { key: 'original', label: 'Original', hint: 'Mismo tamaño, PNG con transparencia', maxW: null, maxH: null, anyOrientation: true, format: 'png', quality: 0.92, maxMB: null },
  { key: 'dreamActor', label: 'Para Dream Actor', hint: 'Hasta 1920×1080 · JPG · 4,5 MB', maxW: 1920, maxH: 1080, anyOrientation: true, format: 'jpeg', quality: 0.9, maxMB: 4.5 },
  { key: 'instagram', label: 'Instagram post', hint: 'Hasta 1080×1350 · JPG', maxW: 1080, maxH: 1350, anyOrientation: false, format: 'jpeg', quality: 0.9, maxMB: null },
  { key: 'story', label: 'Historia / Reel', hint: 'Hasta 1080×1920 · JPG', maxW: 1080, maxH: 1920, anyOrientation: false, format: 'jpeg', quality: 0.9, maxMB: null },
  { key: 'youtube', label: 'Miniatura YouTube', hint: 'Hasta 1280×720 · JPG · 2 MB', maxW: 1280, maxH: 720, anyOrientation: false, format: 'jpeg', quality: 0.9, maxMB: 2 },
  { key: 'web', label: 'Web liviana', hint: 'Lado mayor 1600 · WebP', maxW: 1600, maxH: 1600, anyOrientation: true, format: 'webp', quality: 0.8, maxMB: null },
];

export function normalizeOutput(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  const preset = o.preset === 'custom' || OUTPUT_PRESETS.some((x) => x.key === o.preset) ? o.preset : 'original';
  const side = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : clamp(Math.round(Number(v)), 64, MAX_OUTPUT_PX));
  const mb = o.maxMB == null || o.maxMB === '' || !Number.isFinite(Number(o.maxMB)) || Number(o.maxMB) <= 0
    ? null : clamp(Number(o.maxMB), 0.05, 50);
  return {
    preset,
    maxW: side(o.maxW),
    maxH: side(o.maxH),
    anyOrientation: o.anyOrientation !== false,
    format: MIME[o.format] ? o.format : 'png',
    quality: clamp(num(o.quality, 0.92), 0.4, 1),
    maxMB: mb,
  };
}

/** Elegir un ajuste por destino: copia sus valores. */
export function outputFromPreset(key) {
  const pr = OUTPUT_PRESETS.find((x) => x.key === key) || OUTPUT_PRESETS[0];
  const { label, hint, ...rest } = pr;
  return normalizeOutput({ ...rest, preset: pr.key });
}

export const isDefaultOutput = (o) => {
  const n = normalizeOutput(o);
  return !n.maxW && !n.maxH && n.format === 'png' && !n.maxMB;
};

/** Tamaño final dentro del recuadro, sin deformar ni agrandar. */
export function fitOutputSize(w, h, output) {
  const o = normalizeOutput(output);
  if (!w || !h || (!o.maxW && !o.maxH)) return { width: w, height: h };
  let bw = o.maxW || Infinity;
  let bh = o.maxH || Infinity;
  if (o.anyOrientation && bw !== Infinity && bh !== Infinity) {
    const long = Math.max(bw, bh); const short = Math.min(bw, bh);
    if (w >= h) { bw = long; bh = short; } else { bw = short; bh = long; }
  }
  const k = Math.min(1, bw / w, bh / h);
  return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)) };
}

export const outputExt = (o) => EXT[normalizeOutput(o).format];
export const outputMime = (o) => MIME[normalizeOutput(o).format];

/** «1,2 MB» / «230 KB» */
export function fmtBytes(n) {
  const v = Number(n) || 0;
  if (v >= 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(v / 1024))} KB`;
}

function scaledCanvas(src, w, h, background) {
  // reducción en pasos de ½ para que no quede serruchada
  let cur = src;
  while (cur.width / 2 >= w && cur.height / 2 >= h) {
    const half = document.createElement('canvas');
    half.width = Math.round(cur.width / 2); half.height = Math.round(cur.height / 2);
    const hc = half.getContext('2d');
    hc.imageSmoothingQuality = 'high';
    hc.drawImage(cur, 0, 0, half.width, half.height);
    cur = half;
  }
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const ctx = out.getContext('2d');
  if (background) { ctx.fillStyle = background; ctx.fillRect(0, 0, w, h); }
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(cur, 0, 0, w, h);
  return out;
}

function toBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No se pudo generar la imagen'))), mime, quality);
  });
}

/**
 * Codifica el resultado según `output`: escala al recuadro, formato y calidad;
 * con peso máximo baja la calidad (JPG/WebP) y, si no alcanza, la resolución.
 * JPG no tiene transparencia: se aplana sobre `background` (blanco por defecto).
 */
export async function encodeOutput(canvas, output, { background = null } = {}) {
  const o = normalizeOutput(output);
  const mime = MIME[o.format];
  const bg = o.format === 'jpeg' ? (background || '#FFFFFF') : background;
  let { width, height } = fitOutputSize(canvas.width, canvas.height, o);
  let c = (width !== canvas.width || height !== canvas.height || bg) ? scaledCanvas(canvas, width, height, bg) : canvas;
  let q = o.quality;
  let blob = await toBlob(c, mime, o.format === 'png' ? undefined : q);
  const cap = o.maxMB ? o.maxMB * 1024 * 1024 : null;
  let guard = 0;
  while (cap && blob.size > cap && guard < 14) {
    guard += 1;
    if (o.format !== 'png' && q > 0.5) {
      q = Math.max(0.5, q - 0.08);
    } else {
      width = Math.max(64, Math.round(width * 0.85)); height = Math.max(64, Math.round(height * 0.85));
      c = scaledCanvas(canvas, width, height, bg);
    }
    blob = await toBlob(c, mime, o.format === 'png' ? undefined : q);
  }
  return { blob, width, height, format: o.format, mime, ext: EXT[o.format], quality: o.format === 'png' ? null : q, fits: !cap || blob.size <= cap };
}

function isValidMark(m) {
  if (!m || typeof m !== 'object') return false;
  if (!MARK_TOOLS.some(([k]) => k === m.type) || m.type === 'erase') return false;
  if (!Array.isArray(m.points) || m.points.length < 1) return false;
  return m.type !== 'text' || (typeof m.text === 'string' && m.text.trim().length > 0);
}

export function isIdentityCorners(corners) {
  return corners.every(([x, y], i) => Math.abs(x - IDENTITY_CORNERS[i][0]) < 1e-4
    && Math.abs(y - IDENTITY_CORNERS[i][1]) < 1e-4);
}

export function defaultPolygon(crop = { t: 0, r: 0, b: 0, l: 0 }) {
  const l = crop.l / 100; const r = 1 - crop.r / 100; const t = crop.t / 100; const b = 1 - crop.b / 100;
  return [[l, t], [r, t], [r, b], [l, b]];
}

/** ¿Hay algo para aplicar? */
export function isIdentity(params) {
  const p = normalizeParams(params);
  const d = normalizeParams({});
  return JSON.stringify({ ...p, shape: { ...p.shape, points: null } })
    === JSON.stringify({ ...d, shape: { ...d.shape, points: null } })
    && p.shape.kind === 'rect';
}

/** ¿El resultado tiene partes transparentes? (en la pista base se vería negro) */
export function needsTransparency(params) {
  const p = normalizeParams(params);
  const c = p.crop;
  return !!(c.t || c.r || c.b || c.l || p.fade.side !== 'none' || p.shape.kind !== 'rect'
    || p.shape.radius > 0 || p.warp || p.shadow.enabled);
}

/** Texto corto de lo aplicado: «Recorte · Fundido derecha · Color». */
export function describeEdit(params) {
  const p = normalizeParams(params);
  const out = [];
  const c = p.crop;
  if (c.t || c.r || c.b || c.l) out.push('Recorte');
  const sideLabel = { left: 'izquierda', right: 'derecha', top: 'arriba', bottom: 'abajo' };
  if (p.fade.side === 'radial') out.push('Viñeta');
  else if (p.fade.side !== 'none') out.push(`Fundido ${sideLabel[p.fade.side]}`);
  if (p.warp) out.push('Deformada');
  if (p.shape.kind === 'ellipse') out.push('Elipse');
  else if (p.shape.kind === 'polygon') out.push('Forma libre');
  else if (p.shape.radius > 0) out.push('Redondeada');
  if (p.border.enabled) out.push('Borde');
  if (p.shadow.enabled) out.push('Sombra');
  const col = p.color;
  if (col.brightness !== 100 || col.contrast !== 100 || col.saturation !== 100 || col.blur || col.grayscale) out.push('Color');
  if (p.flip.h || p.flip.v || p.rotate) out.push('Volteada');
  if (p.marks.length) out.push('Anotada');
  if (!isDefaultOutput(p.output)) {
    const pr = OUTPUT_PRESETS.find((x) => x.key === p.output.preset);
    out.push(pr && pr.key !== 'original' ? pr.label : `Tamaño ${EXT[p.output.format].toUpperCase()}`);
  }
  return out.join(' · ');
}

export const PRESETS = [
  ['mitad-izq', 'Mitad izquierda', { crop: { t: 0, r: 50, b: 0, l: 0 } }],
  ['mitad-der', 'Mitad derecha', { crop: { t: 0, r: 0, b: 0, l: 50 } }],
  ['fade-der', 'Desvanecer derecha', { fade: { side: 'right', start: 45, soft: 25 } }],
  ['fade-izq', 'Desvanecer izquierda', { fade: { side: 'left', start: 45, soft: 25 } }],
  ['vineta', 'Viñeta suave', { fade: { side: 'radial', start: 45, soft: 35 } }],
  ['ventana', 'Ventana con borde', {
    crop: { t: 10, r: 10, b: 10, l: 10 }, shape: { kind: 'rect', radius: 4 },
    border: { enabled: true, color: '#FFFFFF', width: 6 }, shadow: { enabled: true, strength: 60 },
  }],
];

/** Un rápido reinicia la geometría y conserva color, giro y anotaciones. */
export function applyPreset(params, id) {
  const preset = PRESETS.find(([k]) => k === id);
  if (!preset) return normalizeParams(params);
  const cur = normalizeParams(params);
  const d = normalizeParams({});
  return normalizeParams({
    ...cur, crop: d.crop, fade: d.fade, shape: d.shape, border: d.border, shadow: d.shadow, warp: null,
    ...preset[2],
  });
}

// ── Geometría ────────────────────────────────────────────────────────────

/** Tamaño de salida (con giro de 90° se intercambian lados), con tope. */
export function outputSize(w, h, rotate = 0, maxPx = MAX_OUTPUT_PX) {
  const swap = rotate === 90 || rotate === 270;
  let ow = swap ? h : w;
  let oh = swap ? w : h;
  const k = Math.min(1, maxPx / Math.max(ow, oh));
  ow = Math.max(1, Math.round(ow * k));
  oh = Math.max(1, Math.round(oh * k));
  return { width: ow, height: oh };
}

function solve8(A, b) {
  const n = 8;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let i = 0; i < n; i += 1) {
    let p = i;
    for (let k = i + 1; k < n; k += 1) if (Math.abs(M[k][i]) > Math.abs(M[p][i])) p = k;
    [M[i], M[p]] = [M[p], M[i]];
    if (Math.abs(M[i][i]) < 1e-12) return null;
    for (let k = i + 1; k < n; k += 1) {
      const f = M[k][i] / M[i][i];
      for (let j = i; j <= n; j += 1) M[k][j] -= f * M[i][j];
    }
  }
  const x = new Array(n);
  for (let i = n - 1; i >= 0; i -= 1) {
    let s = M[i][n];
    for (let j = i + 1; j < n; j += 1) s -= M[i][j] * x[j];
    x[i] = s / M[i][i];
  }
  return x;
}

/** Homografía que lleva los 4 puntos `src` a los 4 `dst`: [a,b,c,d,e,f,g,h]. */
export function homography(src, dst) {
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i += 1) {
    const [x, y] = src[i];
    const [u, v] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  return solve8(A, b);
}

export function applyHomography(h, x, y) {
  const w = h[6] * x + h[7] * y + 1;
  return [(h[0] * x + h[1] * y + h[2]) / w, (h[3] * x + h[4] * y + h[5]) / w];
}

/**
 * Deforma una imagen RGBA (`src`, w×h) para que sus esquinas caigan en
 * `corners` (fracciones de la salida, mismo tamaño). Muestreo bilineal; lo
 * que queda afuera es transparente.
 */
export function warpPixels(src, w, h, corners) {
  const out = new Uint8ClampedArray(w * h * 4);
  const dst = corners.map(([x, y]) => [x * w, y * h]);
  const inv = homography(dst, [[0, 0], [w, 0], [w, h], [0, h]]);
  if (!inv) return out;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const [sx, sy] = applyHomography(inv, x + 0.5, y + 0.5);
      const fx = sx - 0.5;
      const fy = sy - 0.5;
      if (fx < -0.5 || fy < -0.5 || fx > w - 0.5 || fy > h - 0.5) continue;
      const x0 = Math.max(0, Math.min(w - 1, Math.floor(fx)));
      const y0 = Math.max(0, Math.min(h - 1, Math.floor(fy)));
      const x1 = Math.min(w - 1, x0 + 1);
      const y1 = Math.min(h - 1, y0 + 1);
      const ax = Math.max(0, Math.min(1, fx - x0));
      const ay = Math.max(0, Math.min(1, fy - y0));
      const o = (y * w + x) * 4;
      for (let ch = 0; ch < 4; ch += 1) {
        const p00 = src[(y0 * w + x0) * 4 + ch];
        const p10 = src[(y0 * w + x1) * 4 + ch];
        const p01 = src[(y1 * w + x0) * 4 + ch];
        const p11 = src[(y1 * w + x1) * 4 + ch];
        out[o + ch] = (p00 * (1 - ax) + p10 * ax) * (1 - ay) + (p01 * (1 - ax) + p11 * ax) * ay;
      }
    }
  }
  return out;
}

/** Brillo, contraste, saturación y blanco y negro sobre píxeles RGBA (in place). */
export function adjustPixels(data, { brightness = 100, contrast = 100, saturation = 100, grayscale = false } = {}) {
  const br = brightness / 100;
  const ct = contrast / 100;
  const sat = grayscale ? 0 : saturation / 100;
  if (br === 1 && ct === 1 && sat === 1) return data;
  for (let i = 0; i < data.length; i += 4) {
    let r = data[i] * br; let g = data[i + 1] * br; let b = data[i + 2] * br;
    r = (r - 128) * ct + 128; g = (g - 128) * ct + 128; b = (b - 128) * ct + 128;
    const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    data[i] = l + (r - l) * sat; data[i + 1] = l + (g - l) * sat; data[i + 2] = l + (b - l) * sat;
  }
  return data;
}

/** Recuadro visible (px) a partir del recorte. */
export function cropRect(crop, w, h) {
  const x = (crop.l / 100) * w;
  const y = (crop.t / 100) * h;
  return { x, y, w: Math.max(1, w - x - (crop.r / 100) * w), h: Math.max(1, h - y - (crop.b / 100) * h) };
}

/** Puntos del degradé (px) para el lado elegido: [x0,y0,x1,y1] de opaco a transparente. */
export function fadeLine(fade, w, h) {
  const a = fade.start / 100;
  const z = Math.min(1, a + fade.soft / 100);
  switch (fade.side) {
    case 'right': return [a * w, 0, z * w, 0];
    case 'left': return [(1 - a) * w, 0, (1 - z) * w, 0];
    case 'bottom': return [0, a * h, 0, z * h];
    case 'top': return [0, (1 - a) * h, 0, (1 - z) * h];
    default: return null;
  }
}

/** Mueve un punto del polígono / esquina con límites. */
export function movePoint(points, index, x, y, lo = 0, hi = 1) {
  return points.map((p, i) => (i === index ? [clamp(x, lo, hi), clamp(y, lo, hi)] : p));
}

/** Inserta un punto en el lado del polígono más cercano a (x, y). */
export function insertPolygonPoint(points, x, y) {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < points.length; i += 1) {
    const [ax, ay] = points[i];
    const [bx, by] = points[(i + 1) % points.length];
    const dx = bx - ax; const dy = by - ay;
    const len = dx * dx + dy * dy || 1e-9;
    const t = clamp(((x - ax) * dx + (y - ay) * dy) / len, 0, 1);
    const d = (ax + t * dx - x) ** 2 + (ay + t * dy - y) ** 2;
    if (d < bestD) { bestD = d; best = i; }
  }
  const next = points.slice();
  next.splice(best + 1, 0, [clamp(x, 0, 1), clamp(y, 0, 1)]);
  return next;
}

/** Marca bajo el puntero (para la goma). Devuelve el índice o -1. */
export function hitMark(marks, x, y, tol = 0.02) {
  for (let i = marks.length - 1; i >= 0; i -= 1) {
    const xs = marks[i].points.map((p) => p[0]);
    const ys = marks[i].points.map((p) => p[1]);
    const pad = tol + (marks[i].type === 'text' ? 0.03 : 0);
    const x0 = Math.min(...xs) - pad; const x1 = Math.max(...xs) + pad + (marks[i].type === 'text' ? 0.2 : 0);
    const y0 = Math.min(...ys) - pad; const y1 = Math.max(...ys) + pad;
    if (x >= x0 && x <= x1 && y >= y0 && y <= y1) return i;
  }
  return -1;
}

/** Escala de superposición para que la imagen se vea igual que en la pista base (encaje «contain»). */
export function overlayFitScale(imgW, imgH, frameAspect = 16 / 9) {
  if (!imgW || !imgH) return 1;
  const a = imgW / imgH;
  return a >= frameAspect ? 1 : +(a / frameAspect).toFixed(4);
}

// ── Render (necesita canvas: navegador) ─────────────────────────────────

const canvasOf = (w, h) => {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
};

let filterSupport = null;
function supportsCtxFilter() {
  if (filterSupport !== null) return filterSupport;
  try {
    const ctx = canvasOf(1, 1).getContext('2d');
    ctx.filter = 'blur(1px)';
    filterSupport = ctx.filter === 'blur(1px)';
  } catch (_e) { filterSupport = false; }
  return filterSupport;
}

function shapePath(ctx, p, W, H) {
  const r = cropRect(p.crop, W, H);
  ctx.beginPath();
  if (p.shape.kind === 'ellipse') {
    ctx.ellipse(r.x + r.w / 2, r.y + r.h / 2, r.w / 2, r.h / 2, 0, 0, Math.PI * 2);
  } else if (p.shape.kind === 'polygon') {
    p.shape.points.forEach(([x, y], i) => (i ? ctx.lineTo(x * W, y * H) : ctx.moveTo(x * W, y * H)));
    ctx.closePath();
  } else {
    const rad = (p.shape.radius / 100) * Math.min(r.w, r.h);
    if (rad > 0 && ctx.roundRect) ctx.roundRect(r.x, r.y, r.w, r.h, rad);
    else ctx.rect(r.x, r.y, r.w, r.h);
  }
}

function drawMarks(ctx, marks, W, H) {
  const unit = Math.max(W, H) / 1000;
  marks.forEach((m) => {
    ctx.save();
    ctx.strokeStyle = m.color || '#EF4444';
    ctx.fillStyle = m.color || '#EF4444';
    ctx.lineWidth = (m.width || 6) * unit;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const pts = m.points.map(([x, y]) => [x * W, y * H]);
    if (m.type === 'path') {
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.stroke();
    } else if (m.type === 'arrow' && pts.length >= 2) {
      const [[x0, y0], [x1, y1]] = [pts[0], pts[pts.length - 1]];
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      const ang = Math.atan2(y1 - y0, x1 - x0);
      const head = Math.max(14 * unit, ctx.lineWidth * 3);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - head * Math.cos(ang - 0.45), y1 - head * Math.sin(ang - 0.45));
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - head * Math.cos(ang + 0.45), y1 - head * Math.sin(ang + 0.45));
      ctx.stroke();
    } else if ((m.type === 'rect' || m.type === 'ellipse') && pts.length >= 2) {
      const [[x0, y0], [x1, y1]] = [pts[0], pts[pts.length - 1]];
      ctx.beginPath();
      if (m.type === 'rect') ctx.rect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
      else ctx.ellipse((x0 + x1) / 2, (y0 + y1) / 2, Math.abs(x1 - x0) / 2, Math.abs(y1 - y0) / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (m.type === 'text') {
      const size = Math.round((m.size || 32) * unit);
      ctx.font = `700 ${size}px Inter, system-ui, sans-serif`;
      ctx.textBaseline = 'top';
      const [x, y] = pts[0];
      const tw = ctx.measureText(m.text).width;
      ctx.fillStyle = m.color || '#EF4444';
      ctx.fillRect(x - size * 0.3, y - size * 0.2, tw + size * 0.6, size * 1.4);
      ctx.fillStyle = (m.color || '').toUpperCase() === '#FFFFFF' || (m.color || '').toUpperCase() === '#FBBF24' ? '#111111' : '#FFFFFF';
      ctx.fillText(m.text, x, y);
    }
    ctx.restore();
  });
}

/**
 * Dibuja la imagen editada en un canvas nuevo. `maxPx` acota el lado mayor
 * (preview rápido o imagen final). Devuelve el canvas.
 */
export function renderEdit(img, rawParams, { maxPx = MAX_OUTPUT_PX } = {}) {
  const p = normalizeParams(rawParams);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const { width: W, height: H } = outputSize(iw, ih, p.rotate, maxPx);

  // 1) Girar / voltear + color
  let c = canvasOf(W, H);
  let ctx = c.getContext('2d');
  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.rotate((p.rotate * Math.PI) / 180);
  ctx.scale(p.flip.h ? -1 : 1, p.flip.v ? -1 : 1);
  const swap = p.rotate === 90 || p.rotate === 270;
  const dw = swap ? H : W;
  const dh = swap ? W : H;
  const col = p.color;
  const useFilter = supportsCtxFilter();
  if (useFilter) {
    const parts = [];
    if (col.brightness !== 100) parts.push(`brightness(${col.brightness / 100})`);
    if (col.contrast !== 100) parts.push(`contrast(${col.contrast / 100})`);
    if (col.saturation !== 100) parts.push(`saturate(${col.saturation / 100})`);
    if (col.grayscale) parts.push('grayscale(1)');
    if (col.blur) parts.push(`blur(${(col.blur * Math.max(W, H)) / 1000}px)`);
    if (parts.length) ctx.filter = parts.join(' ');
  }
  ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
  ctx.restore();
  if (!useFilter) {
    const data = ctx.getImageData(0, 0, W, H);
    adjustPixels(data.data, col);
    ctx.putImageData(data, 0, 0);
  }

  // 2) Deformar
  if (p.warp) {
    const src = ctx.getImageData(0, 0, W, H);
    const warped = warpPixels(src.data, W, H, p.warp);
    const c2 = canvasOf(W, H);
    c2.getContext('2d').putImageData(new ImageData(warped, W, H), 0, 0);
    c = c2; ctx = c.getContext('2d');
  }

  // 3) Recorte + forma
  const cr = p.crop;
  const masked = cr.t || cr.r || cr.b || cr.l || p.shape.kind !== 'rect' || p.shape.radius > 0;
  if (masked) {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = '#000';
    shapePath(ctx, p, W, H);
    ctx.fill();
    ctx.restore();
  }

  // 4) Fundido
  if (p.fade.side !== 'none') {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    let g;
    if (p.fade.side === 'radial') {
      const R = Math.hypot(W, H) / 2;
      g = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, R);
      g.addColorStop(Math.min(0.999, p.fade.start / 100), 'rgba(0,0,0,1)');
      g.addColorStop(Math.min(1, (p.fade.start + p.fade.soft) / 100), 'rgba(0,0,0,0)');
    } else {
      const [x0, y0, x1, y1] = fadeLine(p.fade, W, H);
      g = ctx.createLinearGradient(x0, y0, x1 === x0 && y1 === y0 ? x0 + 1 : x1, y1);
      g.addColorStop(0, 'rgba(0,0,0,1)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
    }
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }

  // 5) Borde (por dentro de la forma)
  if (p.border.enabled) {
    const bw = (p.border.width * Math.max(W, H)) / 1000;
    ctx.save();
    shapePath(ctx, p, W, H);
    ctx.clip();
    shapePath(ctx, p, W, H);
    ctx.lineWidth = bw * 2;
    ctx.strokeStyle = p.border.color;
    ctx.stroke();
    ctx.restore();
  }

  // 6) Sombra
  if (p.shadow.enabled) {
    const c3 = canvasOf(W, H);
    const x3 = c3.getContext('2d');
    const unit = Math.max(W, H) / 1000;
    x3.shadowColor = `rgba(0,0,0,${(p.shadow.strength / 100) * 0.8})`;
    x3.shadowBlur = 24 * unit;
    x3.shadowOffsetY = 8 * unit;
    x3.drawImage(c, 0, 0);
    c = c3; ctx = x3;
  }

  // 7) Anotaciones (arriba de todo)
  if (p.marks.length) drawMarks(ctx, p.marks, W, H);
  return c;
}

export function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No se pudo generar la imagen'))), 'image/png');
  });
}

/** Nombre de archivo de la versión editada: «Escena 3 · editada.png». */
export function editedFileName(label, kind = 'editada', ext = 'png') {
  const base = String(label || 'Imagen').replace(/\.(png|jpe?g|webp|gif)$/i, '')
    .replace(/ · (editada|anotada)( \d+)?$/i, '').replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 60) || 'Imagen';
  return `${base} · ${kind}.${ext}`;
}
